import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useSwipeToClose from '../../../hooks/useSwipeToClose';
import Spinner from '../../UI/Spinner';

const CuisineDrawer = ({ isOpen, cuisineOptions, selectedCuisines, isLoadingCuisines, setCuisinesAll, closeDrawer, onSave, isSaving = false }) => {
  const { t } = useTranslation('wizard');
  const { containerRef, dragOffset, isAnimating, shouldRender, isVisible, isDragging, DragHandle } = useSwipeToClose(closeDrawer, isOpen);

  // Temporary local state for draft changes
  const [tempSelectedCuisines, setTempSelectedCuisines] = useState(selectedCuisines);
  const [isLimitExceeded, setIsLimitExceeded] = useState(false);
  const [wiggleTrigger, setWiggleTrigger] = useState(false);

  // Reset temp state when drawer opens
  useEffect(() => {
    if (isOpen) {
      setTempSelectedCuisines(selectedCuisines);
      setIsLimitExceeded(false);
      setWiggleTrigger(false);
    }
  }, [isOpen, selectedCuisines]);

  // Reset wiggle trigger after animation finishes
  useEffect(() => {
    if (wiggleTrigger) {
      const timer = setTimeout(() => {
        setWiggleTrigger(false);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [wiggleTrigger]);

  if (!shouldRender) return null;

  const MAX_CUISINES = 3;

  // Handle cuisine selection (local only)
  const handleTempCuisineSelect = (cuisineName) => {
    if (cuisineName === "Surprise Me") {
      setTempSelectedCuisines(["Surprise Me"]);
      setIsLimitExceeded(false);
      return;
    }
    
    // If selecting a specific cuisine, remove "surprise" option
    setTempSelectedCuisines(prev => {
      const newSelection = prev.filter(name => name !== "Surprise Me");
      
      // Toggle the selected cuisine
      if (newSelection.includes(cuisineName)) {
        const updated = newSelection.filter(name => name !== cuisineName);
        if (updated.length < MAX_CUISINES) {
          setIsLimitExceeded(false);
        }
        return updated;
      } else {
        // Enforce max limit
        if (newSelection.length >= MAX_CUISINES) {
          if (!isLimitExceeded) {
            setIsLimitExceeded(true);
            setWiggleTrigger(true);
          }
          return [...newSelection.slice(1), cuisineName];
        }
        return [...newSelection, cuisineName];
      }
    });
  };

  // Save changes to parent and close
  const handleSave = () => {
    // Set all cuisines at once
    setCuisinesAll(tempSelectedCuisines);
    onSave();
  };

  return createPortal(
    <div 
      className={`fixed flex items-end justify-center inset-0 bg-[#0D0C07] z-[200] drawer-overlay`} 
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition: 'background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
      onClick={closeDrawer}
    >
      <div 
        ref={containerRef}
        className={`flex flex-col w-full bg-[#F8F8F7] rounded-[12px_12px_0px_0px] max-w-5xl mx-auto shadow-lg drawer-content max-h-[80vh]`}
        style={{
          transform: isVisible 
            ? `translateY(${Math.max(0, dragOffset)}px)` 
            : 'translateY(100%)',
          transition: (dragOffset === 0 && isAnimating) || (!isDragging && dragOffset === 0)
            ? 'transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
            : 'none'
        }}
        onClick={(e) => e.stopPropagation()}
      >
      {/* Drag Handle */}
      <DragHandle />
      {/* Close button */}
      <button 
        onClick={closeDrawer}
        className="absolute top-1 right-1 flex justify-center items-center md:hover:bg-gray-200 w-[36px] h-[36px] rounded-[12px] transition-colors z-20"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z" fill="#56554D"/>
        </svg>
      </button>
      
      {/* Scrollable content including header */}
      <div className="flex-1 overflow-y-auto min-h-0 overflow-x-hidden p-8 px-6 pb-[116px]">
        {/* Header */}
        <h2 className="
                  text-[var(--Color-Grays-Gray-10,#0D0C07)]
                  text-[16px]
                  font-bold
                  leading-[32px]
                  w-full
                  mb-1
                ">{t('cuisine.title')}</h2>
        <p className={`${isLimitExceeded ? 'text-[var(--Color-UI-Error,#D40B0B)] font-semibold' : 'text-[#65635D]'} ${wiggleTrigger ? 'animate-wiggle' : ''} text-[14px] font-medium leading-[20px] mb-4 transition-colors`}>{t('cuisine.subtitle')}</p>
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {cuisineOptions.map((cuisine, index) => (
            <button
              key={cuisine.name}
              className={`p-3 text-left text-[14px] leading-[24px] font-semibold rounded-[12px] border-[0.5px] border-[#0D0C07] ${
                index === 0 ? 'col-span-2 md:col-span-2 lg:col-span-3' : ''
              } ${
                tempSelectedCuisines.includes(cuisine.name)
                  ? 'bg-[#FFCB00] md:hover:bg-[#FFCB00]/90'
                  : 'bg-white md:hover:bg-[#F8F8F7]'
              }`}
              onClick={() => handleTempCuisineSelect(cuisine.name)}
            >
              {index === 0 ? (
                <div className="flex items-start gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12.1055 2.79541C12.3149 2.81249 12.5171 2.88076 12.6943 2.99365L12.7812 3.0542L12.8623 3.12158C13.0188 3.26189 13.139 3.43807 13.2119 3.63525L13.2441 3.73486L13.249 3.75244L14.5664 8.86475L14.5996 8.96533C14.6393 9.06289 14.6985 9.15208 14.7734 9.22705C14.8731 9.32661 14.9975 9.39766 15.1338 9.43311H15.1348L20.2471 10.7515L20.2617 10.7544L20.3623 10.7876C20.5603 10.8601 20.7379 10.9794 20.8789 11.1362L20.9463 11.2173L21.0068 11.3042C21.1393 11.5113 21.21 11.7532 21.21 12.0005C21.2099 12.283 21.1175 12.5579 20.9463 12.7827C20.7964 12.9796 20.5934 13.1289 20.3623 13.2134L20.2617 13.2456C20.257 13.2469 20.2518 13.2483 20.2471 13.2495L15.1348 14.5659L15.1357 14.5669C14.9989 14.6022 14.8734 14.673 14.7734 14.7729C14.6985 14.8479 14.6393 14.9371 14.5996 15.0347L14.5664 15.1353L13.248 20.2476C13.2466 20.2534 13.2448 20.2594 13.2432 20.2651C13.1672 20.5365 13.0047 20.7755 12.7803 20.9458C12.5558 21.1161 12.2818 21.2084 12 21.2085C11.7181 21.2085 11.4433 21.1162 11.2188 20.9458C11.0225 20.7968 10.8732 20.5954 10.7881 20.3657L10.7559 20.2651C10.7542 20.2593 10.7525 20.2534 10.751 20.2476L9.43359 15.1353C9.39825 14.9983 9.32659 14.873 9.22656 14.7729C9.15158 14.698 9.06238 14.6397 8.96484 14.6001L8.86523 14.5669L3.75195 13.2485C3.74538 13.2468 3.73896 13.2445 3.73242 13.2427C3.46203 13.1659 3.22317 13.0031 3.05371 12.7788C2.9055 12.5826 2.81673 12.3486 2.79688 12.105L2.79297 12.0005L2.79688 11.895C2.81674 11.6514 2.90549 11.4174 3.05371 11.2212L3.12109 11.1401C3.28385 10.9578 3.49589 10.8245 3.73242 10.7573L3.75195 10.7524L8.86523 9.43311L8.96484 9.3999C9.06255 9.36028 9.15147 9.30109 9.22656 9.22607C9.32649 9.12621 9.39816 9.0015 9.43359 8.86475L10.752 3.75244L10.7568 3.73486L10.7891 3.63525C10.8741 3.40519 11.0232 3.20334 11.2197 3.0542L11.3066 2.99365C11.5132 2.8621 11.7536 2.79159 12 2.7915L12.1055 2.79541ZM5.33301 15.2915C5.80275 15.2915 6.18499 15.6619 6.20605 16.1265C6.67103 16.1471 7.04199 16.5304 7.04199 17.0005C7.04176 17.4703 6.67086 17.8519 6.20605 17.8726C6.18553 18.3376 5.80311 18.7085 5.33301 18.7085C4.86306 18.7083 4.48047 18.3375 4.45996 17.8726C3.99549 17.8516 3.62523 17.4701 3.625 17.0005C3.625 16.5307 3.99532 16.1474 4.45996 16.1265C4.481 15.662 4.86342 15.2917 5.33301 15.2915ZM11.1279 9.30225C11.0143 9.74181 10.785 10.1434 10.4639 10.4644C10.1829 10.7451 9.84049 10.9555 9.46484 11.0796L9.30176 11.1274L5.9209 11.9995L9.30176 12.8716L9.46484 12.9194C9.84066 13.0437 10.1828 13.2547 10.4639 13.5356C10.7849 13.8567 11.0144 14.2581 11.1279 14.6978V14.6987L11.999 18.0806L12.8721 14.6978L12.9199 14.5347C13.0442 14.159 13.2552 13.8166 13.5361 13.5356C13.8573 13.2146 14.2586 12.985 14.6982 12.8716L18.0801 11.9995L14.6982 11.1284C14.2585 11.015 13.8573 10.7854 13.5361 10.4644C13.215 10.1433 12.9856 9.74192 12.8721 9.30225V9.30127L12 5.91846L11.1279 9.30225ZM17.792 7.8335V7.0415H17C16.5168 7.04149 16.125 6.64974 16.125 6.1665C16.1251 5.68336 16.5168 5.29152 17 5.2915H17.792V4.50049C17.792 4.01724 18.1837 3.62549 18.667 3.62549C19.1501 3.62565 19.542 4.01734 19.542 4.50049V5.2915H20.333C20.8162 5.2915 21.2079 5.68335 21.208 6.1665C21.208 6.64975 20.8163 7.0415 20.333 7.0415H19.542V7.8335C19.5419 8.31659 19.1501 8.70834 18.667 8.7085C18.1838 8.7085 17.7921 8.31669 17.792 7.8335Z" fill="#0C0C0D"/>
                  </svg>
                  <div>
                    <div className="font-semibold">{t('cuisine.surpriseMe')}</div>
                    <div className="font-normal text-gray-500">{t('cuisine.surpriseMeDescription')}</div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="font-semibold">{t(`cuisine.types.${cuisine.name}`, cuisine.name)}</div>
                  {/*<div className="font-normal text-gray-500">{t(`cuisine.descriptions.${cuisine.name}`, cuisine.description)}</div>*/}
                </>
              )}
            </button>
          ))}
        </div>
      </div>
      
      {/* Fixed Save Button */}
      <div
          className="
            fixed bottom-[16px] left-[20px] right-[20px]
            py-2 px-2 
            flex justify-center
            bg-[linear-gradient(135deg,_rgba(255,255,255,0.6)_0%,_rgba(255,255,255,0.8)_100%)]
            backdrop-blur-lg
            z-50
            max-w-5xl
            mx-auto
            rounded-[96px]
            border-[0.5px] border-[linear-gradient(145deg,_rgba(255,255,255,0.8)_0%,_rgba(255,255,255,1)_100%)]
            shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)]
          " 
        >
        <div className="w-full max-w-5xl">
          <button
            className={`
              inline-flex justify-center items-center gap-1
              h-[48px] px-[24px] py-[14px]
              rounded-[32px]
              border-[1px] border-[linear-gradient(200deg,_#090_0%,_#060_100%)]
              bg-[#090]
              text-[#FFF] text-center
              text-[14px] font-medium leading-[28px]
              transition-colors
              w-full
              shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4)]
              ${
                isSaving
                  ? "cursor-not-allowed opacity-60 bg-[#090]"
                  : "cursor-pointer bg-[linear-gradient(200deg,_#090_0%,_#060_100%)] md:hover:border-[0.5px] md:hover:border-[#060] md:hover:bg-[#060]"
              }
            `}
            onClick={isSaving ? undefined : handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Spinner size="sm" color="#FFF" />
                <span>{t('common:buttons.saving', 'Saving...')}</span>
              </>
            ) : (
              <span>{t('common:buttons.save', 'Save')}</span>
            )}
          </button>
        </div>
      </div>
      </div>
    </div>, document.body
  );
};

export default CuisineDrawer;
